import { useState } from "react";
import {
  Autocomplete,
  Collapse,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import Button from "../editor/toolbar/Button";
import { ComplianceTrainingReportRow } from "../../common/ComplianceTraining";
import { exportMultiSheetExcel } from "../../util/excelDocument";
import { Fleet } from "../../common/Fleet";
import { Vessel } from "../../common/Vessel";
import { useGetFleetsQuery } from "../../redux/api/fleet";
import { useGetVesselsQuery } from "../../redux/api/vessel";
import { useSelector } from "react-redux";
import { selectCurrentUser } from "../../redux/authentication/authSlice";
import { UserGroup } from "../../common/User";
import "./ComplianceTrainingReport.scss";

interface ComplianceTrainingReportProps {
  rows: ComplianceTrainingReportRow[] | undefined;
  onClose?: (bol: boolean) => void;
}

const ComplianceTrainingReport = ({ rows, onClose }: ComplianceTrainingReportProps) => {
  const user = useSelector(selectCurrentUser);
  const isVesselExec = (user?.userGroup as UserGroup) === UserGroup.VESSEL_EXECS;

  const [fleet, setFleet] = useState<Fleet | null>(null);
  const [vessel, setVessel] = useState<Vessel | null>(null);
  const [summaryExpanded, setSummaryExpanded] = useState(false);
  const [expandedFleets, setExpandedFleets] = useState<Set<string>>(new Set());

  const { data: fleets, isLoading: isFleetsLoading } = useGetFleetsQuery(
    {},
    { refetchOnMountOrArgChange: true, skip: isVesselExec }
  );
  const { data: vessels, isLoading: isVesselsLoading } = useGetVesselsQuery({});

  const getVesselsList = () => {
    if (isVesselExec) {
      return vessels
        ? (vessels as Vessel[]).filter(v => !v.disabled && (rows ?? []).some(r => r.vesselName === v.name))
        : [];
    }
    if (fleet) return fleet.vessels.filter((v) => !v.disabled);
    return vessels ? (vessels as Vessel[]).filter((v) => !v.disabled) : [];
  };

  const filteredRows = (rows ?? []).filter((r) => {
    if (vessel) return r.vesselName === vessel.name;
    if (fleet) return r.fleetName === fleet.name;
    return true;
  });

  // Group by vessel when a fleet/vessel is active or user is vessel exec; otherwise group by fleet.
  const groupByVessel = !!vessel || !!fleet || isVesselExec;
  const summaryColumnLabel = groupByVessel ? "Vessel" : "Fleet";
  const showTotalRow = !vessel;
  const totalRowLabel = fleet ? `${fleet.name} Fleet Total` : "Total";

  type SummaryEntry = { name: string; percentage: number; complete: boolean };

  const groupRows = (rowSet: ComplianceTrainingReportRow[], keyFn: (r: ComplianceTrainingReportRow) => string): SummaryEntry[] => {
    const grouped = new Map<string, ComplianceTrainingReportRow[]>();
    for (const row of rowSet) {
      const key = keyFn(row);
      if (!grouped.has(key)) grouped.set(key, []);
      grouped.get(key)!.push(row);
    }
    return Array.from(grouped.entries())
      .map(([name, grpRows]) => ({
        name,
        percentage: Math.round(grpRows.reduce((sum: number, r: ComplianceTrainingReportRow) => sum + r.completedPercentage, 0) / grpRows.length),
        complete: grpRows.every((r: ComplianceTrainingReportRow) => r.complete),
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  };

  const summaryGroups: SummaryEntry[] = filteredRows.length > 0
    ? groupRows(filteredRows, (r) => groupByVessel ? r.vesselName : r.fleetName)
    : [];

  // In all-fleets view, also compute vessel breakdowns per fleet for expand/collapse.
  const vesselSummariesByFleet = new Map<string, SummaryEntry[]>();
  if (!groupByVessel) {
    const byFleet = new Map<string, ComplianceTrainingReportRow[]>();
    for (const row of filteredRows) {
      if (!byFleet.has(row.fleetName)) byFleet.set(row.fleetName, []);
      byFleet.get(row.fleetName)!.push(row);
    }
    for (const [fleetName, fleetRows] of Array.from(byFleet.entries())) {
      vesselSummariesByFleet.set(fleetName, groupRows(fleetRows, (r) => r.vesselName));
    }
  }

  const toggleFleet = (fleetName: string) => {
    setExpandedFleets((prev) => {
      const next = new Set(prev);
      if (next.has(fleetName)) next.delete(fleetName);
      else next.add(fleetName);
      return next;
    });
  };

  const overallPercentage = filteredRows.length > 0
    ? Math.round(filteredRows.reduce((sum, r) => sum + r.completedPercentage, 0) / filteredRows.length)
    : null;
  const overallComplete = filteredRows.length > 0 && filteredRows.every((r) => r.complete);

  const closeDialog = () => {
    if (!!onClose) onClose(false);
  };

  const exportFile = async () => {
    const summaryData = summaryGroups.flatMap((g: SummaryEntry) => [
      ...(!groupByVessel
        ? (vesselSummariesByFleet.get(g.name) ?? []).map((v: SummaryEntry) => ({
            name: v.name,
            completedPercentage: `${v.percentage}%`,
          }))
        : [{ name: g.name, completedPercentage: `${g.percentage}%` }]),
      ...(!groupByVessel
        ? [{ name: `${g.name} Fleet Total`, completedPercentage: `${g.percentage}%`, _bold: true }, { name: "", completedPercentage: "" }]
        : []),
    ]);
    if (showTotalRow && overallPercentage !== null) {
      summaryData.push({ name: totalRowLabel, completedPercentage: `${overallPercentage}%`, _bold: true });
    }

    await exportMultiSheetExcel({
      documentName: "ComplianceTrainingReport",
      sheets: [
        {
          sheetName: "Report",
          data: filteredRows.map((r) => ({
            fleet: r.fleetName,
            vessel: r.vesselName,
            training: r.trainingName,
            completedPercentage: r.completedPercentage,
          })),
          columns: [
            { label: "Fleet", name: "fleet", width: 20 },
            { label: "Vessel", name: "vessel", width: 20 },
            { label: "Training Name", name: "training", width: 30 },
            { label: "Completed Percentage", name: "completedPercentage", width: 15 },
          ],
        },
        ...(summaryData.length > 0
          ? [{
              sheetName: "Summary",
              data: summaryData,
              columns: [
                { label: summaryColumnLabel, name: "name", width: 25 },
                { label: "Completion %", name: "completedPercentage", width: 15 },
              ],
            }]
          : []),
      ],
    });
  };

  return (
    <div>
      <div className="flex align-items-center justify-content-btwn mb-1">
        <h1 className="mr-2">Compliance Training Report</h1>
        <div className="flex g-1">
          <Button onClick={exportFile}>EXPORT TO EXCEL</Button>
          {!!onClose && (
            <Button onClick={closeDialog}>
              <CloseIcon />
            </Button>
          )}
        </div>
      </div>

      <div className="filters-wrap mb-2">
        <div className="filters-list flex g-2 align-items-center">
          {!isVesselExec && (
            <label style={{ width: "200px" }}>
              <span>Fleet:</span>
              <Autocomplete
                options={fleets ? (fleets as Fleet[]) : []}
                value={fleet}
                onChange={(_, value) => { setFleet(value); setVessel(null); }}
                renderInput={(params) => <TextField {...params} />}
                isOptionEqualToValue={(opt, val) => opt.id === val.id}
                getOptionLabel={(opt) => opt.name ?? ""}
                disabled={isFleetsLoading}
              />
            </label>
          )}
          <label style={{ width: "200px" }}>
            <span>Vessel:</span>
            <Autocomplete
              options={getVesselsList()}
              value={vessel}
              onChange={(_, value) => setVessel(value)}
              renderInput={(params) => <TextField {...params} />}
              isOptionEqualToValue={(opt, val) => opt.id === val.id}
              getOptionLabel={(opt) => opt.name ?? ""}
              disabled={isVesselsLoading}
            />
          </label>
        </div>
      </div>

      {summaryGroups.length > 0 && (
        <div className="mb-2">
          <div className="summary-toggle flex align-items-center g-1 mb-1" onClick={() => setSummaryExpanded(!summaryExpanded)}>
            <h2>Completion Summary</h2>
            {summaryExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </div>
          <Collapse in={summaryExpanded}>
            <Table size="small" className="wd-fit-content">
              <TableHead>
                <TableRow>
                  <TableCell className="b-600">{summaryColumnLabel}</TableCell>
                  <TableCell className="b-600">Completion %</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {summaryGroups.map((g: SummaryEntry) => (
                  <>
                    <TableRow
                      key={g.name}
                      className={!groupByVessel ? "summary-fleet-row" : undefined}
                      onClick={!groupByVessel ? () => toggleFleet(g.name) : undefined}
                    >
                      <TableCell className={!groupByVessel ? "b" : undefined}>
                        <div className="flex align-items-center g-1">
                          {!groupByVessel && (
                            expandedFleets.has(g.name) ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />
                          )}
                          {g.name}
                        </div>
                      </TableCell>
                      <TableCell className={g.complete ? "color-green b-600" : "color-red b-600"}>
                        {g.percentage}%
                      </TableCell>
                    </TableRow>
                    {!groupByVessel && expandedFleets.has(g.name) && vesselSummariesByFleet.get(g.name)?.map((v: SummaryEntry) => (
                      <TableRow key={`${g.name}-${v.name}`}>
                        <TableCell className="summary-vessel-indent">{v.name}</TableCell>
                        <TableCell className={v.complete ? "color-green b-600" : "color-red b-600"}>
                          {v.percentage}%
                        </TableCell>
                      </TableRow>
                    ))}
                  </>
                ))}
                {showTotalRow && overallPercentage !== null && (
                  <TableRow>
                    <TableCell className="b">{totalRowLabel}</TableCell>
                    <TableCell className={overallComplete ? "color-green b" : "color-red b"}>
                      {overallPercentage}%
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Collapse>
        </div>
      )}

      <Table>
        <TableHead>
          <TableRow>
            <TableCell className="b-600">Fleet</TableCell>
            <TableCell className="b-600">Vessel</TableCell>
            <TableCell className="b-600">Training Name</TableCell>
            <TableCell className="b-600">Completed Percentage</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {filteredRows.map((row, index) => (
            <TableRow key={index}>
              <TableCell>{row.fleetName}</TableCell>
              <TableCell>{row.vesselName}</TableCell>
              <TableCell>{row.trainingName}</TableCell>
              <TableCell style={{ color: row.complete ? "green" : "red", fontWeight: 600 }}>
                {`${row.completedPercentage}%`}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

export default ComplianceTrainingReport;
